import { apiSlice } from './apiSlice'

export const FUTURES_ALL_ROWS_PAGE_SIZE = 1000000

export interface FuturesMvListResponse {
  items: Record<string, unknown>[]
  total: number
  page: number
  pageSize: number
}

export interface FuturesMvListQuery {
  page: number
  pageSize: number
  q?: string
  /** Mặc định true — khớp filter Access `frmFuturesQryFuturesOrder`. */
  onlyNotFullyAllocated?: boolean
  /** `yes` | `no` — omit or `all` for no filter (not sent). Purchase tab. */
  is_forced_close?: 'yes' | 'no' | 'all'
  /** `yes` | `no` — omit or `all` for no filter (not sent). SA Contract tab. */
  is_finalized?: 'yes' | 'no' | 'all'
}

/** Slim row cho combobox Contract P/S ở dialog adjust-lot. */
export interface AdjustLotContractOption {
  contractNo: string
  objectName: string | null
  contractDate: string | null
}

/** `form_frmFuturesQryLocalPriceAdd` / subform datasheet — draft + save payload. */
export interface FuturesLocalPriceDraftRow {
  dateActual: string
  qualityCode: string
  price: number | null
  priceUnit: string
}

export interface FuturesLocalPriceDraftResponse {
  inputDate: string
  fx: number | null
  rows: FuturesLocalPriceDraftRow[]
}

export interface FuturesLocalPriceSaveRequest {
  inputDate: string
  fx: number | null
  rows: FuturesLocalPriceDraftRow[]
}

/** Chỉ phân trang — không tham số lọc ngoài query Access. */
export type FuturesMvPageQuery = Pick<FuturesMvListQuery, 'page' | 'pageSize'>

export interface FuturesReportQuery extends FuturesMvPageQuery {
  kind: string
  /** Tab Check Futures: ld | ny */
  variant?: 'ld' | 'ny'
}

export interface FuturesBankListQuery extends Pick<FuturesMvListQuery, 'page' | 'pageSize' | 'q'> {
  bank: string
}

export interface FuturesColumnsPreference {
  columns: string[] | null
  defaultColumns: string[] | null
}

/** Header date filter của Futures Control area — lưu trong `user_preferences`. */
export interface FuturesControlPagePreferences {
  dateFrom: string
  dateTo: string
}

export interface FuturesControlListFiltersPatch {
  dateFrom?: string
  dateTo?: string
}

export type FuturesColumnsMode =
  | 'pucontract'
  | 'sacontract'
  | 'orders'
  | 'spreads'
  | 'allocation'
  | 'allocation-noalc'
  | 'market'
  | 'bank-cash'
  | 'bank-statement'
  | 'adjust-lot'
  | 'bank-cost-change'

const futuresControlApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getFuturesColumnsPreference: builder.query<FuturesColumnsPreference, FuturesColumnsMode>({
      query: (mode) => ({ url: `/futures-control/preferences/columns/${mode}` }),
      providesTags: ['FuturesControl'],
    }),

    putFuturesColumnsPreference: builder.mutation<
      { message: string },
      { mode: FuturesColumnsMode; columns: string[]; defaultColumns?: string[] }
    >({
      query: ({ mode, columns, defaultColumns }) => ({
        url: `/futures-control/preferences/columns/${mode}`,
        method: 'PUT',
        body: { columns, ...(defaultColumns ? { defaultColumns } : {}) },
      }),
      invalidatesTags: ['FuturesControl'],
    }),

    getFuturesControlPagePreferences: builder.query<FuturesControlPagePreferences, void>({
      query: () => ({ url: '/futures-control/page-preferences' }),
      providesTags: ['FuturesControl'],
    }),

    patchFuturesControlListFilters: builder.mutation<{ message: string }, FuturesControlListFiltersPatch>({
      query: (body) => ({
        url: '/futures-control/list-filters',
        method: 'PATCH',
        body,
      }),
      // BE đọc filter từ user_preferences cho mọi list query → invalidate để các
      // tab tự refetch với filter mới (parity SaleContract `patchSaleContractListFilters`).
      invalidatesTags: ['FuturesControl'],
    }),

    getFuturesOrders: builder.query<FuturesMvListResponse, FuturesMvListQuery>({
      query: ({ page, pageSize, q, onlyNotFullyAllocated }) => ({
        url: '/futures-control/orders-futures',
        params: {
          page,
          pageSize,
          ...(q ? { q } : {}),
          ...(onlyNotFullyAllocated === false ? { onlyNotFullyAllocated: false } : {}),
        },
      }),
      providesTags: ['FuturesControl'],
    }),

    getFuturesOrderOptions: builder.query<string[], void>({
      query: () => ({ url: '/futures-control/orders-futures/options' }),
      providesTags: ['FuturesControl'],
    }),

    /**
     * Single-row lookup bypass date filter — dùng cho autofill dialog Allocation
     * (Buy/Sell + Not Allocated lot). Phải fresh mỗi lần chọn ID để không lệ
     * thuộc list query bị filter date ở page level.
     * `keepUnusedDataFor: 0` → cache drop ngay khi không subscriber, lần sau mở
     * dialog refetch chứ không dùng stale.
     */
    getFuturesOrderDetail: builder.query<
      { orderNumber: string; buyOrSell?: string; notAllocated?: number | string },
      string
    >({
      query: (orderNumber) => ({
        url: `/futures-control/orders-futures/details/${encodeURIComponent(orderNumber)}`,
      }),
      keepUnusedDataFor: 0,
      providesTags: ['FuturesControl'],
    }),

    suggestFuturesOrderNo: builder.query<{ orderNumber: string }, void>({
      query: () => ({ url: '/futures-control/orders-futures/suggest-order-no' }),
      transformResponse: (response: { suggestedNo: string }) => ({ orderNumber: response.suggestedNo }),
      providesTags: ['FuturesControl'],
    }),

    /** Pre-save check (skill access-suggest) — Futures Orders. */
    checkFuturesOrderNoExists: builder.query<{ exists: boolean }, { orderNo: string }>({
      query: ({ orderNo }) => ({
        url: '/futures-control/orders-futures/check-order-no',
        params: { orderNo },
      }),
      keepUnusedDataFor: 0,
    }),

    /** Access RowSource `frmFuturesQryFuturesOrderAdd.symbol` → `public.market_calendar.full_symbol` (year ∈ [now-2, now+4]). */
    getFuturesOrderSymbols: builder.query<string[], void>({
      query: () => ({ url: '/futures-control/orders-futures/lookups/symbols' }),
      providesTags: ['FuturesControl'],
    }),

    /** Access RowSource `frmFuturesQryFuturesOrderAdd.type_of_trade` → `public.trade_type`. */
    getFuturesOrderTradeTypes: builder.query<string[], void>({
      query: () => ({ url: '/futures-control/orders-futures/lookups/trade-types' }),
      providesTags: ['FuturesControl'],
    }),

    /** Access RowSource `frmFuturesQryFuturesOrderAdd.partner` → `public.bank` (Vietinbank/BIDV/MBbank/Internal). */
    getFuturesOrderPartners: builder.query<string[], void>({
      query: () => ({ url: '/futures-control/orders-futures/lookups/partners' }),
      providesTags: ['FuturesControl'],
    }),

    /** Access RowSource `frmFuturesQryFuturesOrderAdd.flag` → `public.flag`. */
    getFuturesOrderFlags: builder.query<Array<{ flag: string; description: string }>, void>({
      query: () => ({ url: '/futures-control/orders-futures/lookups/flags' }),
      providesTags: ['FuturesControl'],
    }),

    getFuturesSpreads: builder.query<FuturesMvListResponse, FuturesMvListQuery>({
      query: ({ page, pageSize, q }) => ({
        url: '/futures-control/orders-spreads',
        params: {
          page,
          pageSize,
          ...(q ? { q } : {}),
        },
      }),
      providesTags: ['FuturesControl'],
    }),

    /** Access `btnNew_Click` — `Suggest_IndexName("orders_spreads")`. */
    suggestSpreadOrderNo: builder.query<{ orderNumber: string }, void>({
      query: () => ({ url: '/futures-control/orders-spreads/suggest-order-no' }),
      transformResponse: (response: { suggestedNo: string }) => ({ orderNumber: response.suggestedNo }),
      providesTags: ['FuturesControl'],
    }),

    /** Pre-save check (skill access-suggest) — Futures Spreads. */
    checkSpreadOrderNoExists: builder.query<{ exists: boolean }, { orderNo: string }>({
      query: ({ orderNo }) => ({
        url: '/futures-control/orders-spreads/check-order-no',
        params: { orderNo },
      }),
      keepUnusedDataFor: 0,
    }),

    /** Access RowSource `frmFuturesQrySpreadsAdd.symbol` → `public.market_calendar.full_symbol` (year ∈ [now-2, now+4]). */
    getSpreadSymbols: builder.query<string[], void>({
      query: () => ({ url: '/futures-control/orders-spreads/lookups/symbols' }),
      providesTags: ['FuturesControl'],
    }),

    /** Distinct values for Spreads filter bar (Date/B/S/Partner/Type) — distinct từ `mv_orders_spreads`. */
    getFuturesSpreadsFilterValues: builder.query<
      { dates: string[]; buyOrSell: string[]; partner: string[]; typeOfTrade: string[] },
      void
    >({
      query: () => ({ url: '/futures-control/orders-spreads/lookups/filter-values' }),
      providesTags: ['FuturesControl'],
    }),

    getFuturesAllocations: builder.query<FuturesMvListResponse, FuturesMvListQuery>({
      query: ({ page, pageSize, q }) => ({
        url: '/futures-control/allocations',
        params: {
          page,
          pageSize,
          ...(q ? { q } : {}),
        },
      }),
      providesTags: ['FuturesControl'],
    }),

    getFuturesAllocationsNoalc: builder.query<FuturesMvListResponse, FuturesMvListQuery>({
      query: ({ page, pageSize, q }) => ({
        url: '/futures-control/allocations-noalc',
        params: {
          page,
          pageSize,
          ...(q ? { q } : {}),
        },
      }),
      providesTags: ['FuturesControl'],
    }),

    getMarketPrices: builder.query<FuturesMvListResponse, FuturesMvPageQuery>({
      query: ({ page, pageSize }) => ({
        url: '/futures-control/market-prices',
        params: { page, pageSize },
      }),
      providesTags: ['FuturesControl'],
    }),

    getLocalPFutures: builder.query<FuturesMvListResponse, FuturesMvListQuery>({
      query: ({ page, pageSize, q, is_forced_close }) => ({
        url: '/futures-control/local-p-futures',
        params: {
          page,
          pageSize,
          ...(q ? { q } : {}),
          ...(is_forced_close && is_forced_close !== 'all' ? { is_forced_close } : {}),
        },
      }),
      providesTags: ['FuturesControl'],
    }),

    getLocalPContractOptions: builder.query<string[], void>({
      query: () => ({ url: '/futures-control/local-p-futures/options' }),
      providesTags: ['FuturesControl'],
    }),

    /** Single-row lookup bypass date filter — autofill Balance lot trong dialog Allocation. */
    getLocalPFutureDetail: builder.query<
      { contractNo: string; balance?: number | string },
      string
    >({
      query: (contractNo) => ({
        url: `/futures-control/local-p-futures/details/${encodeURIComponent(contractNo)}`,
      }),
      keepUnusedDataFor: 0,
      providesTags: ['FuturesControl'],
    }),

    getLocalS: builder.query<FuturesMvListResponse, FuturesMvListQuery>({
      query: ({ page, pageSize, q, is_finalized }) => ({
        url: '/futures-control/local-s',
        params: {
          page,
          pageSize,
          ...(q ? { q } : {}),
          ...(is_finalized && is_finalized !== 'all' ? { is_finalized } : {}),
        },
      }),
      providesTags: ['FuturesControl'],
    }),

    getLocalSContractOptions: builder.query<string[], void>({
      query: () => ({ url: '/futures-control/local-s/options' }),
      providesTags: ['FuturesControl'],
    }),

    /** Single-row lookup bypass date filter — autofill Lot to fix trong dialog Allocation. */
    getLocalSDetail: builder.query<
      { contractSale: string; lotToFix?: number | string },
      string
    >({
      query: (contractSale) => ({
        url: `/futures-control/local-s/details/${encodeURIComponent(contractSale)}`,
      }),
      keepUnusedDataFor: 0,
      providesTags: ['FuturesControl'],
    }),

    /** Slim list cho combobox Contract P ở dialog Add/Edit của tab adjust-lot.
     *  Source: `local_p` LEFT JOIN `cp_vn` — chỉ contract_no, object_name, contract_date. */
    getLocalPAdjustLotOptions: builder.query<AdjustLotContractOption[], void>({
      query: () => ({ url: '/futures-control/local-p-futures/adjust-lot-options' }),
      providesTags: ['FuturesControl'],
    }),

    /** Slim list cho combobox Contract S ở dialog Add/Edit của tab adjust-lot.
     *  Source: `local_s` LEFT JOIN `cp_vn` — `contract_sale` alias `contract_no`. */
    getLocalSAdjustLotOptions: builder.query<AdjustLotContractOption[], void>({
      query: () => ({ url: '/futures-control/local-s/adjust-lot-options' }),
      providesTags: ['FuturesControl'],
    }),

    /** Physical Price dialog — unit combobox options (`form_frmFuturesQryLocalPriceAdd_subForm` cột Unit). */
    getFuturesLocalPricePriceUnits: builder.query<{ options: string[] }, void>({
      query: () => ({ url: '/futures-control/local-price/price-units' }),
      providesTags: ['FuturesControl'],
    }),

    /** Draft rows + FX — tương đương Access `Form_Load` / `date_actual_AfterUpdate` (nạp tmp + FX). */
    getFuturesLocalPriceDraft: builder.query<FuturesLocalPriceDraftResponse, { inputDate: string }>({
      query: ({ inputDate }) => ({
        url: '/futures-control/local-price/draft',
        params: { inputDate },
      }),
      providesTags: ['FuturesControl'],
    }),

    saveFuturesLocalPrice: builder.mutation<{ message: string }, FuturesLocalPriceSaveRequest>({
      query: (body) => ({ url: '/futures-control/local-price/save', method: 'POST', body }),
      invalidatesTags: ['FuturesControl'],
    }),

    getAdjustLotRows: builder.query<FuturesMvListResponse, FuturesMvListQuery>({
      query: ({ page, pageSize, q }) => ({
        url: '/futures-control/adjust-lot-rows',
        params: { page, pageSize, ...(q ? { q } : {}) },
      }),
      providesTags: ['FuturesControl'],
    }),

    getBankCostChangeRows: builder.query<FuturesMvListResponse, FuturesMvListQuery>({
      query: ({ page, pageSize, q }) => ({
        url: '/futures-control/bank-cost-change-rows',
        params: { page, pageSize, ...(q ? { q } : {}) },
      }),
      providesTags: ['FuturesControl'],
    }),

    getFuturesReport: builder.query<FuturesMvListResponse, FuturesReportQuery>({
      query: ({ kind, page, pageSize, variant }) => ({
        url: `/futures-control/reports/${encodeURIComponent(kind)}`,
        params: {
          page,
          pageSize,
          ...(kind === 'check-futures' && variant ? { variant } : {}),
        },
      }),
      providesTags: ['FuturesControl'],
    }),

    refreshCheckFutures: builder.mutation<{ ld: number; ny: number }, void>({
      query: () => ({ url: '/futures-control/reports/check-futures/refresh', method: 'POST' }),
      invalidatesTags: ['FuturesControl'],
    }),

    saveCheckFutures: builder.mutation<{ ld: number; ny: number }, { ld: string[]; ny: string[] }>({
      query: (body) => ({ url: '/futures-control/reports/check-futures/save', method: 'POST', body }),
      invalidatesTags: ['FuturesControl'],
    }),

    /** Margin Report — per-bank+month detail (rpFuturesMargin DLookUp `v_rp_futures_margin_bank`). */
    getMarginReportBankDetail: builder.query<FuturesMvListResponse, FuturesMvPageQuery | void>({
      query: (args) => ({
        url: '/futures-control/reports/margin/bank-detail',
        params: args ?? { page: 1, pageSize: FUTURES_ALL_ROWS_PAGE_SIZE },
      }),
      providesTags: ['FuturesControl'],
    }),

    /** Margin Report — key/value config consumed by Change Input Value dialog. */
    getMarginReportChangeValues: builder.query<Record<string, string>, void>({
      query: () => '/futures-control/reports/margin/change-values',
      providesTags: ['FuturesControl'],
    }),

    putMarginReportChangeValues: builder.mutation<Record<string, string>, Record<string, string | number>>({
      query: (body) => ({
        url: '/futures-control/reports/margin/change-values',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['FuturesControl'],
    }),

    getFuturesBankCash: builder.query<FuturesMvListResponse, FuturesBankListQuery>({
      query: ({ bank, page, pageSize, q }) => ({
        url: '/futures-control/bank-cash',
        params: { bank, page, pageSize, ...(q ? { q } : {}) },
      }),
      providesTags: ['FuturesControl'],
    }),

    getFuturesBankStatement: builder.query<FuturesMvListResponse, FuturesBankListQuery>({
      query: ({ bank, page, pageSize, q }) => ({
        url: '/futures-control/bank-statement',
        params: { bank, page, pageSize, ...(q ? { q } : {}) },
      }),
      providesTags: ['FuturesControl'],
    }),

    getBankStatementLatest: builder.query<
      { item: Record<string, unknown> | null },
      { bank: string; before?: string }
    >({
      query: ({ bank, before }) => ({
        url: '/futures-control/bank-statement/latest',
        params: { bank, ...(before ? { before } : {}) },
      }),
      providesTags: ['FuturesControl'],
    }),

    createFuturesOrder: builder.mutation<Record<string, unknown>, Record<string, unknown>>({
      query: (body) => ({ url: '/futures-control/orders-futures', method: 'POST', body }),
      invalidatesTags: ['FuturesControl'],
    }),
    bulkCreateFuturesOrders: builder.mutation<
      { created: number; orderNumbers: string[] },
      { rows: Record<string, unknown>[] }
    >({
      query: (body) => ({ url: '/futures-control/orders-futures/mass', method: 'POST', body }),
      invalidatesTags: ['FuturesControl'],
    }),
    updateFuturesOrder: builder.mutation<
      Record<string, unknown>,
      { orderNumber: string; body: Record<string, unknown> }
    >({
      query: ({ orderNumber, body }) => ({
        url: `/futures-control/orders-futures/${encodeURIComponent(orderNumber)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['FuturesControl'],
    }),
    deleteFuturesOrder: builder.mutation<void, string>({
      query: (orderNumber) => ({
        url: `/futures-control/orders-futures/${encodeURIComponent(orderNumber)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FuturesControl'],
    }),

    createFuturesSpread: builder.mutation<Record<string, unknown>, Record<string, unknown>>({
      query: (body) => ({ url: '/futures-control/orders-spreads', method: 'POST', body }),
      invalidatesTags: ['FuturesControl'],
    }),
    bulkCreateFuturesSpreads: builder.mutation<
      { created: number; orderNumbers: string[] },
      { rows: Record<string, unknown>[] }
    >({
      query: (body) => ({ url: '/futures-control/orders-spreads/mass', method: 'POST', body }),
      invalidatesTags: ['FuturesControl'],
    }),
    updateFuturesSpread: builder.mutation<
      Record<string, unknown>,
      { orderNumber: string; body: Record<string, unknown> }
    >({
      query: ({ orderNumber, body }) => ({
        url: `/futures-control/orders-spreads/${encodeURIComponent(orderNumber)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['FuturesControl'],
    }),
    deleteFuturesSpread: builder.mutation<void, string>({
      query: (orderNumber) => ({
        url: `/futures-control/orders-spreads/${encodeURIComponent(orderNumber)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FuturesControl'],
    }),

    createFuturesAllocationRow: builder.mutation<Record<string, unknown>, Record<string, unknown>>({
      query: (body) => ({ url: '/futures-control/allocations-row', method: 'POST', body }),
      invalidatesTags: ['FuturesControl'],
    }),
    updateFuturesAllocationRow: builder.mutation<Record<string, unknown>, Record<string, unknown>>({
      query: (body) => ({ url: '/futures-control/allocations-row', method: 'PUT', body }),
      invalidatesTags: ['FuturesControl'],
    }),
    deleteFuturesAllocationRow: builder.mutation<
      void,
      { date: string; contractId: string; futuresId: string }
    >({
      query: ({ date, contractId, futuresId }) => ({
        url: '/futures-control/allocations-row',
        method: 'DELETE',
        params: { date, contract_id: contractId, futures_id: futuresId },
      }),
      invalidatesTags: ['FuturesControl'],
    }),

    createAdjustLot: builder.mutation<Record<string, unknown>, Record<string, unknown>>({
      query: (body) => ({ url: '/futures-control/adjust-lot', method: 'POST', body }),
      invalidatesTags: ['FuturesControl'],
    }),
    updateAdjustLot: builder.mutation<
      Record<string, unknown>,
      { contractNo: string; body: Record<string, unknown> }
    >({
      query: ({ contractNo, body }) => ({
        url: `/futures-control/adjust-lot/${encodeURIComponent(contractNo)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['FuturesControl'],
    }),
    deleteAdjustLot: builder.mutation<void, string>({
      query: (contractNo) => ({
        url: `/futures-control/adjust-lot/${encodeURIComponent(contractNo)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FuturesControl'],
    }),

    createBankCostChange: builder.mutation<Record<string, unknown>, Record<string, unknown>>({
      query: (body) => ({ url: '/futures-control/bank-cost-change', method: 'POST', body }),
      invalidatesTags: ['FuturesControl'],
    }),
    updateBankCostChange: builder.mutation<
      Record<string, unknown>,
      { id: number; body: Record<string, unknown> }
    >({
      query: ({ id, body }) => ({
        url: `/futures-control/bank-cost-change/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['FuturesControl'],
    }),
    deleteBankCostChange: builder.mutation<void, number>({
      query: (id) => ({
        url: `/futures-control/bank-cost-change/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FuturesControl'],
    }),

    upsertBankCash: builder.mutation<Record<string, unknown>, Record<string, unknown>>({
      query: (body) => ({ url: '/futures-control/bank-cash/upsert', method: 'POST', body }),
      invalidatesTags: ['FuturesControl'],
    }),
    deleteBankCash: builder.mutation<void, { date: string; bank: string }>({
      query: ({ date, bank }) => ({
        url: '/futures-control/bank-cash',
        method: 'DELETE',
        params: { date, bank },
      }),
      invalidatesTags: ['FuturesControl'],
    }),

    upsertBankStatement: builder.mutation<Record<string, unknown>, Record<string, unknown>>({
      query: (body) => ({ url: '/futures-control/bank-statement/upsert', method: 'POST', body }),
      invalidatesTags: ['FuturesControl'],
    }),
    deleteBankStatement: builder.mutation<void, { date: string; bank: string }>({
      query: ({ date, bank }) => ({
        url: '/futures-control/bank-statement',
        method: 'DELETE',
        params: { date, bank },
      }),
      invalidatesTags: ['FuturesControl'],
    }),

    createMarketPrice: builder.mutation<Record<string, unknown>, Record<string, unknown>>({
      query: (body) => ({ url: '/futures-control/market-price', method: 'POST', body }),
      invalidatesTags: ['FuturesControl'],
    }),
    updateMarketPrice: builder.mutation<Record<string, unknown>, Record<string, unknown>>({
      query: (body) => ({ url: '/futures-control/market-price', method: 'PUT', body }),
      invalidatesTags: ['FuturesControl'],
    }),
    deleteMarketPrice: builder.mutation<void, { contract: string; exchange: string; timing: string }>({
      query: ({ contract, exchange, timing }) => ({
        url: '/futures-control/market-price',
        method: 'DELETE',
        params: { contract, exchange, timing },
      }),
      invalidatesTags: ['FuturesControl'],
    }),

    createLocalP: builder.mutation<Record<string, unknown>, Record<string, unknown>>({
      query: (body) => ({ url: '/futures-control/local-p', method: 'POST', body }),
      invalidatesTags: ['FuturesControl'],
    }),
    updateLocalP: builder.mutation<Record<string, unknown>, { contractNo: string; body: Record<string, unknown> }>({
      query: ({ contractNo, body }) => ({
        url: `/futures-control/local-p/${encodeURIComponent(contractNo)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['FuturesControl'],
    }),
    deleteLocalP: builder.mutation<void, string>({
      query: (contractNo) => ({
        url: `/futures-control/local-p/${encodeURIComponent(contractNo)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FuturesControl'],
    }),

    createLocalS: builder.mutation<Record<string, unknown>, Record<string, unknown>>({
      query: (body) => ({ url: '/futures-control/local-s', method: 'POST', body }),
      invalidatesTags: ['FuturesControl'],
    }),
    updateLocalS: builder.mutation<Record<string, unknown>, { contractSale: string; body: Record<string, unknown> }>({
      query: ({ contractSale, body }) => ({
        url: `/futures-control/local-s/${encodeURIComponent(contractSale)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['FuturesControl'],
    }),
    deleteLocalS: builder.mutation<void, string>({
      query: (contractSale) => ({
        url: `/futures-control/local-s/${encodeURIComponent(contractSale)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FuturesControl'],
    }),
  }),
})

// Legacy preference endpoints (`/futures-control/preferences/columns/{mode}`
// + `/futures-control/page-preferences` + `/futures-control/list-filters`)
// đã migrate sang generic registry — `usePreference` / `usePreferenceColumns`
// dùng `(scope='futures', key='columns'|'page_filter')`. BE endpoint cũ giữ
// thêm 2 tuần để rollback an toàn, sau đó drop kèm endpoint route.
export const {
  useGetFuturesOrdersQuery,
  useLazyGetFuturesOrdersQuery,
  useGetFuturesOrderOptionsQuery,
  useGetFuturesOrderDetailQuery,
  useSuggestFuturesOrderNoQuery,
  useLazySuggestFuturesOrderNoQuery,
  useLazyCheckFuturesOrderNoExistsQuery,
  useLazyCheckSpreadOrderNoExistsQuery,
  useGetFuturesSpreadsQuery,
  useSuggestSpreadOrderNoQuery,
  useLazySuggestSpreadOrderNoQuery,
  useGetSpreadSymbolsQuery,
  useGetFuturesSpreadsFilterValuesQuery,
  useGetFuturesOrderSymbolsQuery,
  useGetFuturesOrderTradeTypesQuery,
  useGetFuturesOrderPartnersQuery,
  useGetFuturesOrderFlagsQuery,
  useGetFuturesAllocationsQuery,
  useGetFuturesAllocationsNoalcQuery,
  useLazyGetFuturesAllocationsNoalcQuery,
  useGetMarketPricesQuery,
  useGetLocalPFuturesQuery,
  useLazyGetLocalPFuturesQuery,
  useGetLocalPContractOptionsQuery,
  useGetLocalPFutureDetailQuery,
  useGetLocalSQuery,
  useLazyGetLocalSQuery,
  useGetLocalSContractOptionsQuery,
  useGetLocalSDetailQuery,
  useGetLocalPAdjustLotOptionsQuery,
  useGetLocalSAdjustLotOptionsQuery,
  useGetAdjustLotRowsQuery,
  useGetBankCostChangeRowsQuery,
  useGetFuturesReportQuery,
  useRefreshCheckFuturesMutation,
  useSaveCheckFuturesMutation,
  useGetMarginReportBankDetailQuery,
  useGetMarginReportChangeValuesQuery,
  usePutMarginReportChangeValuesMutation,
  useGetFuturesBankCashQuery,
  useGetFuturesBankStatementQuery,
  useGetBankStatementLatestQuery,
  useLazyGetBankStatementLatestQuery,
  useCreateFuturesOrderMutation,
  useBulkCreateFuturesOrdersMutation,
  useUpdateFuturesOrderMutation,
  useDeleteFuturesOrderMutation,
  useCreateFuturesSpreadMutation,
  useBulkCreateFuturesSpreadsMutation,
  useUpdateFuturesSpreadMutation,
  useDeleteFuturesSpreadMutation,
  useCreateFuturesAllocationRowMutation,
  useUpdateFuturesAllocationRowMutation,
  useDeleteFuturesAllocationRowMutation,
  useCreateAdjustLotMutation,
  useUpdateAdjustLotMutation,
  useDeleteAdjustLotMutation,
  useCreateBankCostChangeMutation,
  useUpdateBankCostChangeMutation,
  useDeleteBankCostChangeMutation,
  useUpsertBankCashMutation,
  useDeleteBankCashMutation,
  useUpsertBankStatementMutation,
  useDeleteBankStatementMutation,
  useCreateMarketPriceMutation,
  useUpdateMarketPriceMutation,
  useDeleteMarketPriceMutation,
  useCreateLocalPMutation,
  useUpdateLocalPMutation,
  useDeleteLocalPMutation,
  useCreateLocalSMutation,
  useUpdateLocalSMutation,
  useDeleteLocalSMutation,
  useGetFuturesLocalPricePriceUnitsQuery,
  useGetFuturesLocalPriceDraftQuery,
  useLazyGetFuturesLocalPriceDraftQuery,
  useSaveFuturesLocalPriceMutation,
} = futuresControlApi

export default futuresControlApi
